import { useState } from 'react';
import { Check, MapPin } from 'lucide-react';
import { toast } from 'react-toastify';
import { MAX_SCREEN_SELECTION } from '@/features/campaigns/constants/campaignOptions';
import { useManualAlertOptional } from '@/features/campaigns/components/ManualBuilder/ManualAlert';
import { formatMoney } from '@/shared/lib/format/money';
import { getOptimizedImageUrl } from '@/shared/lib/media/optimizeImageUrl';
import { screenLocationLabel } from '../screensStepHelpers';
import type { ScreenListItem } from '../ScreensStep.types';

type ScreenCardProps = {
  screen: ScreenListItem;
  selected: boolean;
  selectedCount: number;
  /** Soft hint only — all orientations remain selectable. */
  recommended?: boolean;
  /** When true, matching badges are omitted (active filter already communicates it). */
  hideMatchBadge?: boolean;
  onSelect: (screen: Record<string, unknown>) => void;
};

const SCREENISTA_MARK_SRC = '/favicon.svg';

/** Orientation-aware device frame with official mark when media is unavailable. */
function ScreenLogoFallback({ orientation }: { orientation: string }) {
  const isPortrait = orientation === 'PORTRAIT';

  return (
    <div
      className="relative flex h-full w-full items-center justify-center overflow-hidden bg-[#EEF1F5]"
      aria-hidden="true"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          background:
            'radial-gradient(120% 80% at 50% 0%, rgba(255,255,255,0.95) 0%, transparent 55%), radial-gradient(90% 70% at 80% 100%, rgba(163,91,174,0.08) 0%, transparent 50%), radial-gradient(80% 60% at 10% 90%, rgba(17,76,137,0.07) 0%, transparent 45%)',
        }}
      />
      <div
        className={`relative z-[1] flex items-center justify-center rounded-[11px] border border-black/[0.08] bg-[#0B1220] shadow-[0_12px_28px_-14px_rgba(15,23,42,0.55)] ${
          isPortrait ? 'h-[86%] aspect-[9/16]' : 'w-[86%] aspect-[16/10]'
        }`}
      >
        <div className="absolute inset-[3px] overflow-hidden rounded-[8px] bg-gradient-to-b from-[#151D2E] to-[#0A0F1A]">
          <div className="absolute inset-0 flex items-center justify-center">
            <img
              src={SCREENISTA_MARK_SRC}
              alt=""
              className={`${isPortrait ? 'h-[34%]' : 'h-[46%]'} w-auto max-w-[58%] object-contain drop-shadow-[0_4px_12px_rgba(0,0,0,0.35)]`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export function ScreenCard({
  screen,
  selected,
  selectedCount,
  recommended = false,
  hideMatchBadge = false,
  onSelect,
}: ScreenCardProps) {
  const alert = useManualAlertOptional();
  const originalUrl = screen.thumbnail?.url || '';
  const optimizedUrl = getOptimizedImageUrl(originalUrl, { width: 480 });
  const [currentSrc, setCurrentSrc] = useState(optimizedUrl || originalUrl);
  const [imgFailed, setImgFailed] = useState(!originalUrl);

  const orientation = String(
    screen.screenOrientation || screen.screenType || '',
  ).toUpperCase();
  const name = screen.screenName || screen.name || 'Screen';
  const location = screenLocationLabel(screen);
  const isMaxReached = !selected && selectedCount >= MAX_SCREEN_SELECTION;
  const showMatch = recommended && !hideMatchBadge && !selected && !isMaxReached;

  const handleImgError = () => {
    if (currentSrc === optimizedUrl && originalUrl && optimizedUrl !== originalUrl) {
      setCurrentSrc(originalUrl);
    } else {
      setImgFailed(true);
    }
  };

  const handleClick = () => {
    if (isMaxReached) {
      if (alert) {
        alert.warning(
          'Screen limit reached',
          `You already selected ${MAX_SCREEN_SELECTION} displays. Deselect one to choose this screen.`,
          { actions: [{ label: 'Got it', primary: true }] },
        );
      } else {
        toast.info(
          `Selection limit reached (${MAX_SCREEN_SELECTION} displays). Deselect a display to choose this one.`,
          { toastId: 'max-screens-selection' },
        );
      }
      return;
    }
    onSelect(screen as unknown as Record<string, unknown>);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-pressed={selected}
      aria-disabled={isMaxReached}
      title={name}
      className={`group relative flex flex-col overflow-hidden rounded-[16px] border bg-white text-left transition-all duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--dash-brand)] ${
        selected
          ? 'border-[var(--dash-brand)]/50 shadow-[0_0_0_1px_var(--dash-brand),0_8px_24px_-12px_rgba(41,121,255,0.35)]'
          : isMaxReached
            ? 'cursor-not-allowed border-black/[0.06] opacity-50'
            : 'border-black/[0.06] hover:border-black/[0.12] hover:shadow-[0_10px_28px_-14px_rgba(16,38,73,0.28)]'
      }`}
    >
      {/* Preview ~55–60% visual weight */}
      <div className="relative h-[148px] w-full shrink-0 overflow-hidden sm:h-[136px] lg:h-[142px]">
        {!imgFailed && currentSrc ? (
          <img
            src={currentSrc}
            alt=""
            className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
            onError={handleImgError}
          />
        ) : (
          <ScreenLogoFallback orientation={orientation} />
        )}

        <div className="absolute top-2.5 right-2.5 z-10">
          <span
            className={`flex h-6 w-6 items-center justify-center rounded-full transition-all ${
              selected
                ? 'bg-[var(--dash-brand)] text-white shadow-[0_2px_8px_-2px_rgba(41,121,255,0.65)]'
                : 'border border-white/40 bg-black/30 text-transparent backdrop-blur-[3px] group-hover:border-white/70'
            }`}
            aria-hidden="true"
          >
            <Check className="h-3 w-3 stroke-[3]" />
          </span>
        </div>

        {showMatch ? (
          <span className="absolute top-2.5 left-2.5 z-10 rounded-full bg-[var(--dash-brand)]/95 px-2 py-0.5 text-[9px] font-semibold tracking-wide text-white shadow-sm">
            Match
          </span>
        ) : null}

        {orientation === 'PORTRAIT' || orientation === 'LANDSCAPE' ? (
          <span className="absolute right-2.5 bottom-2.5 z-10 rounded-full bg-black/55 px-2 py-0.5 text-[9px] font-semibold tracking-wide text-white/90 backdrop-blur-[3px]">
            {orientation === 'PORTRAIT' ? 'Portrait' : 'Landscape'}
          </span>
        ) : null}
      </div>

      {/* Info + pricing */}
      <div className="flex min-h-0 flex-1 flex-col px-3 pt-2.5 pb-3">
        <div className="min-w-0">
          <h3 className="m-0 line-clamp-2 text-[13px] leading-[1.3] font-semibold tracking-[-0.015em] text-[var(--dash-ink)]">
            {name}
          </h3>
          <p className="mt-1 mb-0 flex min-w-0 items-center gap-1 text-[11.5px] leading-tight text-[var(--dash-muted)]">
            <MapPin className="h-3 w-3 shrink-0 opacity-70" aria-hidden="true" />
            <span className="truncate" title={location}>
              {location}
            </span>
          </p>
        </div>

        <div className="mt-2.5 flex items-center justify-between gap-2 border-t border-black/[0.05] pt-2">
          <p className="m-0 text-[12.5px] font-semibold tabular-nums tracking-[-0.01em] text-[var(--dash-ink)]">
            {formatMoney(screen.basePrice ?? 0)}
            <span className="ml-0.5 text-[11px] font-medium text-[var(--dash-muted)]">
              / sec
            </span>
          </p>
          <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
              selected
                ? 'bg-[var(--dash-brand)]/10 text-[var(--dash-brand)]'
                : isMaxReached
                  ? 'text-[#9CA3AF]'
                  : 'text-[#9CA3AF] group-hover:text-[var(--dash-ink)]'
            }`}
          >
            {selected ? 'Selected' : isMaxReached ? 'Limit' : 'Select'}
          </span>
        </div>
      </div>
    </button>
  );
}
